<template>
  <form @submit.prevent="submit">
    <fg-input label="Name" type="text" v-model="product.name"
      :error="valErrors['name'] ? valErrors['name'][0] : null" />
    <fg-input label="Kurzbezeichnung" type="text" v-model="product.short_name"
      :error="valErrors['short_name'] ? valErrors['short_name'][0] : null" icon="fa-question-circle"
      icon-tooltip="In bestimmten Fällen kann es Sinn machen nur eine Abkürzung des Produkts anzuzeigen, geben Sie hier also falls erforderlich eine Abkürzung für das Produkt ein" />
    <div class="form-group has-label">
      <label>Produktkategorie</label>
      <multiselect v-model="product.product_category" :options="productCategories" track-by="id" label="name" />
    </div>
    <fg-input label="PZN" type="text" v-model="product.pzn" :error="valErrors['pzn'] ? valErrors['pzn'][0] : null" />
    <fg-input label="Hersteller" type="text" v-model="product.hersteller"
      :error="valErrors['hersteller'] ? valErrors['hersteller'][0] : null" />
    <div class="form-group has-label">
      <label>Einheit</label>
      <multiselect v-model="product.unit" :options="units" track-by="id" label="name" />
    </div>
    <fg-input label="Einheiten/Pkg" type="number" v-model="product.stk_pkg"
      :error="valErrors['stk_pkg'] ? valErrors['stk_pkg'][0] : null" />
    <fg-input label="Größe" type="text" v-model="product.size"
      :error="valErrors['size'] ? valErrors['size'][0] : null" />
    <price-input v-if="product.stk_pkg === 0 || product.stk_pkg === 1" :price="product.standard_price"
      label="Standard Einkaufspreis (netto)" @input="product.standard_price = $event" />
    <div class="row" v-else>
      <price-input :price="product.standard_price" class="col-sm-6" label="Standard Einkaufspreis (netto)"
        @input="product.standard_price = $event" />
      <price-input :price="unitPrice(product.standard_price, product.stk_pkg)" class="col-sm-6" label="Preis / Einheit"
        @input="product.standard_price = $event" :disabled="true" />
    </div>
    <div class="row">
      <div class="col-sm-9">
        <fg-input label="Verkaufspreis (brutto)" type="text" :value="price" @input="price = $event"
          :error="valErrors['price'] ? valErrors['price'][0] : null" />
      </div>
      <div class="col-sm-3">
        <div class="form-group has-label">
          <label>USt</label>
          <multiselect v-model="product.tax" :options="sortedTaxOptions.map(taxOption => taxOption.value)"
            :custom-label="customLabel" :allow-empty="false" deselect-label="" />
        </div>
      </div>
    </div>
    <div class="form-group has-label" :class="valErrors['barcode'] ? 'has-error' : ''">
      <label>Barcode</label>
      <div class="input-group">
        <input @input="handleBarcodeScan(product.barcode, 'barcode')" @keydown="preventEnterKey" aria-describedby="addon-right addon-left"
          type="text" class="form-control" v-model="product.barcode">
        <div class="input-group-append">
          <button class="btn btn-fill btn-secondary" type="button" style="margin-bottom: 0"
            @click="generateBarcode('barcode')">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>
      <div class="text-danger invalid-feedback" style="display: block;" v-if="valErrors['barcode']">
        {{ valErrors['barcode'][0] }}
      </div>
    </div>
    <div class="form-group has-label" :class="valErrors['barcode'] ? 'has-error' : ''">
      <label>
        Barcode 2
        <span
          v-tooltip.top-center="'Hier kann ein 2. alternativer Barcode angewendet werden. Hilfreich z.B. wenn ein Produkt 2 Barcodes hat, oder intern andere Barcodes vergeben werden sollen'">
          <i class="fas fa-question-circle" aria-hidden="true"></i>
        </span>
      </label>
      <div class="input-group">
        <input @input="handleBarcodeScan(product.barcode_2, 'barcode2')" aria-describedby="addon-right addon-left"
          type="text" class="form-control" v-model="product.barcode_2">
        <div class="input-group-append">
          <button class="btn btn-fill btn-secondary" type="button" style="margin-bottom: 0"
            @click="generateBarcode('barcode_2')">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>
      <div class="text-danger invalid-feedback" style="display: block;" v-if="valErrors['barcode_2']">
        {{ valErrors['barcode_2'][0] }}
      </div>
    </div>
    <fg-input label="Barcode type" type="text" v-model="product.barcode_type"
      :error="valErrors['barcode_type'] ? valErrors['barcode_type'][0] : null" />
    <div class="form-group has-label">
      <label>Nächster Ablaufdatumstermin</label>
      <el-date-picker v-model="product.next_expiry_date" format="dd.MM.yyyy" value-format="yyyy-MM-dd"
        :picker-options="{ firstDayOfWeek: 1 }" />
    </div>
    <fg-input label="Hat Ablaufdatum">
      <l-checkbox v-model="product.has_ablaufdatum">&nbsp;</l-checkbox>
    </fg-input>
    <fg-input label="Kostenstelle erforderlich" icon="fa-question-circle"
      icon-tooltip="Ist dieser Haken gesetzt, kann das Produkt nur ausgebucht werden, wenn eine Kostenstelle angegeben wird">
      <l-checkbox v-model="product.kostennelle_req">&nbsp;</l-checkbox>
    </fg-input>
    <!-- Kostenstelle -->
    <fg-input label="Buchhaltungs-Konto" type="text" v-model="product.accounting_number"
      :error="valErrors['accounting_number'] ? valErrors['accounting_number'][0] : null" />
    <fg-input label="EAN / Artikelnummer" type="text" v-model="product.referenznummer"
      :error="valErrors['referenznummer'] ? valErrors['referenznummer'][0] : null" />
    <fg-input label="Lagerort">
      <textarea class="form-control" v-model="product.storage_location" rows="3"></textarea>
    </fg-input>
    <fg-input label="Regal" type="text" v-model="product.regal"
      :error="valErrors['regal'] ? valErrors['regal'][0] : null" />
    <fg-input label="Regal 2" type="text" v-model="product.regal_2"
      :error="valErrors['regal_2'] ? valErrors['regal_2'][0] : null" />
    <fg-input label="Kommentar">
      <textarea class="form-control" v-model="product.comment" rows="3"></textarea>
    </fg-input>
    <div class="form-group has-label">
      <label>Alternativen</label>
      <multiselect v-model="product.alternatives" :options="alternatives" :multiple="true" track-by="id" label="name"
        :allow-empty="true" :close-on-select="true" :searchable="true" :internal-search="false" :clear-on-select="false"
        @search-change="getAlternatives" />
    </div>

    <div class="form-group has-label">
      <label>Bild</label> <br>
      <div style="padding: 5px;" v-if="this.url.includes('edit') && product.path">
        <img style="width: 50px;" :src="baseUrlWithoutApi + '/storage/' + product.path" alt="">
      </div>
      <input type="file" @change="uploadImage" />
    </div>

    <button type="submit" class="btn btn-fill btn-info">{{ submitText }}</button>
  </form>
</template>

<script>
import priceWatcher from './price-watcher'
import debounce from 'lodash/debounce'
import PriceInput from 'src/components/price-input'
import { DatePicker } from 'element-ui'
import baseApiUrl from '@/api.js';


export default {
  props: ['valErrors', 'initProduct', 'submitText'],
  components: {
    PriceInput,
    [DatePicker.name]: DatePicker
  },
  mixins: [priceWatcher],
  data() {
    return {
      product: this.initProduct,
      productCategories: [],
      units: [],
      alternatives: [],
      taxOptions: [
      ],
      url: window.location.href
    }
  },
  methods: {
    unitPrice(price, stk_pkg) {
      return (price / stk_pkg).toFixed(1)
    },
    customLabel(taxOption) {
      const removeTrailingZeros = (value) => parseFloat(value).toString();

      const trimmedTaxOption = removeTrailingZeros(taxOption);
      const selectedOption = this.taxOptions.find(option => {
        const trimmedOptionValue = removeTrailingZeros(option.value);
        return trimmedOptionValue === trimmedTaxOption;
      });

      return selectedOption ? selectedOption.label : '';
    },

    preventEnterKey(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    },

    async uploadImage(event) {
      const img = event.target.files[0];
      this.product.image = img;

      const formData = new FormData();
      const id = this.product.id
      formData.append('image', this.product.image);
      formData.append('id', this.product.id);
      if (this.url.includes('edit')) {
        const response = await this.$http.post(`product/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

    },
    submit() {
      this.product.product_category_id = this.product.product_category ? this.product.product_category.id : null
      this.product.unit_id = this.product.unit ? this.product.unit.id : null
      this.$emit('submit', this.product)
    },
    getPrice() {
      return this.product.price
    },
    setPrice(price) {
      this.product.price = price
    },
    getAlternatives: debounce(function (query) {
      const id = this.product.id

      if (query.length >= 3) {
        this.isLoading = true
        this.$http.get(`/products?search=${encodeURIComponent(query)}${id ? '&exclude=' + id : ''}`).then(({ data }) => {
          this.alternatives = data
        })
      }
    }, 300),
    async generateBarcode(barcode) {
      const { data } = await this.$http.get('/barcode/generate')
      this.product[barcode] = data
    },
    async handleBarcodeScan(code, type) {

      let isGS1 = false;
      const gs1Patterns = ['01', '10', '17', '18', '21'];

      for (let pattern of gs1Patterns) {
        if (code.includes(pattern)) {
          isGS1 = true;
          break;
        }
      }
      if (isGS1) {
        try {
          const { data } = await this.$http.get(`/product/decode/${code}`);
          this.identifiers = data.identifiers;
        } catch (error) {
          console.error("Error fetching identifiers:", error);
          this.codeError = true;
          throw error;
        }


        Object.entries(this.identifiers).forEach(([key, item]) => {

          // GTIN
          if (key === "01") {
            if (type === "barcode") {
              this.product.barcode = item.content
            } else {
              this.product.barcode_2 = item.content

            }
          }
          this.product.barcode_type = "gs1-128";
        });
      }
      isGS1 = false;

    }
  },
  created() {
    this.$http.get(`/product_categories`).then(({ data }) => {
      this.productCategories = data
    })
    this.$http.get(`/units`).then(({ data }) => {
      this.units = data
    })
    this.$http.get(`/settings`).then(({ data }) => {
      this.taxOptions = JSON.parse(data.taxes)
    })

  },
  computed: {
    baseUrlWithoutApi() {
      const apiIndex = baseApiUrl.lastIndexOf('/api');

      return apiIndex !== -1 ? baseApiUrl.substring(0, apiIndex) : baseApiUrl;
    },
    sortedTaxOptions() {
      return this.taxOptions.slice().sort((a, b) => b.value - a.value);
    },

  }
}
</script>

<style></style>
